import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  UserCheck, 
  UserMinus, 
  ExternalLink, 
  MoreVertical, 
  Filter,
  Mail,
  Building2,
  ShieldAlert,
  PlusCircle,
  X,
  Copy,
  CheckCircleIcon
} from 'lucide-react';

// --- DUMMY DATA ---
const INITIAL_ADMINS = [
  { id: 1, name: "Ravi Kumar", email: "ravi.kumar@gtpl.com", company: "GTPL", status: "Active", lastLogin: "10 mins ago" },
  { id: 2, name: "Anjali Sharma", email: "a.sharma@infosys.com", company: "Infosys", status: "Active", lastLogin: "2 hours ago" },
  { id: 3, name: "Vikram Singh", email: "v.singh@tcs.com", company: "TCS", status: "Disabled", lastLogin: "5 days ago" },
  { id: 4, name: "Siddharth Jain", email: "sid.j@gtpl.com", company: "GTPL", status: "Active", lastLogin: "Just now" },
  { id: 5, name: "Priya Das", email: "p.das@wipro.com", company: "Wipro", status: "Active", lastLogin: "Yesterday" },
];

// --- STYLING TOKENS ---
const styles = {
  container: "p-8 max-w-7xl mx-auto animate-in fade-in duration-500",
  tableWrapper: "bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden",
  th: "px-6 py-4 text-left text-[11px] font-bold text-gray-400 uppercase tracking-widest bg-gray-50/50 border-b border-gray-100",
  td: "px-6 py-4 text-sm text-gray-600 border-b border-gray-50 transition-colors",
  row: "hover:bg-indigo-50/30 transition-colors group",
  statusActive: "bg-emerald-50 text-emerald-600 border border-emerald-100",
  statusPending: "bg-amber-50 text-amber-600 border border-amber-100",
  statusDisabled: "bg-rose-50 text-rose-600 border border-rose-100",
  badge: "px-2 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center gap-1",
  btnAction: "p-1.5 rounded-md transition-all hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-100",
  btnPrimary: "bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2",
};

export default function SuperAdmins() {
  const [admins, setAdmins] = useState(INITIAL_ADMINS);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [generatedOTP, setGeneratedOTP] = useState(null);

  // Generate random 8-char OTP (letters + numbers + 1 symbol)
  const generateOTP = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let otp = "";
    for (let i = 0; i < 7; i++) {
      otp += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Add one special char at the end
    otp += "!@#$%^&*()_+-=[]{}|;:,.<>?".charAt(Math.floor(Math.random() * 20));
    return otp;
  };

  const handleCreateAdmin = (e) => {
    e.preventDefault();
    setEmailError("");

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailError("Please enter a valid company email address");
      return;
    }

    // Check if email already exists
    if (admins.some(a => a.email.toLowerCase() === newEmail.toLowerCase())) {
      setEmailError("This email is already registered");
      return;
    }

    const otp = generateOTP();

    // Simulate company name from email domain
    const company = newEmail.split('@')[1]?.split('.')[0]?.toUpperCase() || "Unknown";

    const newAdmin = {
      id: admins.length + 1,
      name: "New Super Admin", // In real app → ask for name or auto-generate
      email: newEmail,
      company,
      status: "Pending Activation",
      lastLogin: "Never",
      otp // only for demo — in real app, send via email & don't show/store here
    };

    setAdmins([...admins, newAdmin]);
    setGeneratedOTP(otp);
    setNewEmail("");
    // In real app: send email with OTP here (API call)
    alert(`Super Admin created for ${newEmail}\nOne-time Password: ${otp}\n(Email would be sent in production)`);
  };

  const toggleStatus = (id) => {
    setAdmins(admins.map(admin => {
      if (admin.id === id) {
        const newStatus = admin.status === "Active" ? "Disabled" : "Active";
        return { ...admin, status: newStatus };
      }
      return admin;
    }));
  };

  const filteredAdmins = admins.filter(a => 
    a.name.toLowerCase().includes(search.toLowerCase()) || 
    a.company.toLowerCase().includes(search.toLowerCase()) ||
    a.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.container}>
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-poppins font-bold text-2xl text-gray-900">Super Admin Registry</h1>
          <p className="text-sm text-gray-500 mt-1">Manage infrastructure-level permissions and access states.</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search admins..." 
              className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all w-64"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button 
            onClick={() => setShowCreateModal(true)}
            className={styles.btnPrimary}
          >
            <PlusCircle size={16} /> Create Super Admin
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors">
            <Filter size={14} /> Filter
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className={styles.tableWrapper}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={styles.th}>Administrator</th>
              <th className={styles.th}>Assigned Company</th>
              <th className={styles.th}>Access Status</th>
              <th className={styles.th}>Last Activity</th>
              <th className={styles.th + " text-right"}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.map((admin) => (
              <tr key={admin.id} className={styles.row}>
                <td className={styles.td}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs border border-indigo-200">
                      {admin.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 leading-none mb-1">{admin.name}</p>
                      <div className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Mail size={10} /> {admin.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className={styles.td}>
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-gray-300" />
                    <span className="font-medium">{admin.company}</span>
                  </div>
                </td>
                <td className={styles.td}>
                  <span className={`${styles.badge} ${
                    admin.status === 'Active' ? styles.statusActive :
                    admin.status === 'Pending Activation' ? styles.statusPending :
                    styles.statusDisabled
                  }`}>
                    <div className={`w-1 h-1 rounded-full ${
                      admin.status === 'Active' ? 'bg-emerald-500' :
                      admin.status === 'Pending Activation' ? 'bg-amber-500' :
                      'bg-rose-500'
                    }`} />
                    {admin.status}
                  </span>
                </td>
                <td className={styles.td}>
                  <span className="text-xs text-gray-400 font-medium">{admin.lastLogin}</span>
                </td>
                <td className={styles.td}>
                  <div className="flex items-center justify-end gap-2">
                    <button 
                      onClick={() => toggleStatus(admin.id)}
                      title={admin.status === "Active" ? 'Disable Account' : 'Enable Account'}
                      className={`${styles.btnAction} ${admin.status === 'Active' ? 'text-rose-400 hover:text-rose-600' : 'text-emerald-400 hover:text-emerald-600'}`}
                    >
                      {admin.status === 'Active' ? <UserMinus size={16} /> : <UserCheck size={16} />}
                    </button>
                    <button className={`${styles.btnAction} text-gray-400 hover:text-indigo-600`}>
                      <ExternalLink size={16} />
                    </button>
                    <button className={`${styles.btnAction} text-gray-400 hover:text-gray-600`}>
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* EMPTY STATE */}
        {filteredAdmins.length === 0 && (
          <div className="py-20 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-200 mb-4">
              <ShieldAlert size={32} />
            </div>
            <p className="text-gray-500 font-medium">No administrators found</p>
            <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>

      {/* FOOTER STATS */}
      <div className="mt-6 flex items-center justify-between text-[11px] font-bold text-gray-400 uppercase tracking-widest px-2">
        <p>Showing {filteredAdmins.length} Super Administrators</p>
        <div className="flex gap-4">
          <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500"/> {admins.filter(a => a.status === 'Active').length} Active</span>
          <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-amber-500"/> {admins.filter(a => a.status === 'Pending Activation').length} Pending</span>
          <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-rose-500"/> {admins.filter(a => a.status === 'Disabled').length} Disabled</span>
        </div>
      </div>

      {/* CREATE SUPER ADMIN MODAL */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200"
            >
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-5 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShieldAlert size={24} />
                  <h3 className="text-lg font-bold">Create New Super Admin</h3>
                </div>
                <button 
                  onClick={() => { 
                    setShowCreateModal(false); 
                    setNewEmail(""); 
                    setEmailError(""); 
                    setGeneratedOTP(null); 
                  }}
                  className="p-1.5 hover:bg-indigo-800 rounded-full transition"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {!generatedOTP ? (
                  <form onSubmit={handleCreateAdmin} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="admin@company.com"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                          required
                        />
                      </div>
                      {emailError && (
                        <p className="mt-2 text-sm text-rose-600">{emailError}</p>
                      )}
                    </div>

                    <p className="text-xs text-gray-500">
                      An auto-generated one-time password will be created and sent to this email. The admin must use it to activate their account.
                    </p>

                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowCreateModal(false)}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition shadow-sm"
                      >
                        Create & Send OTP
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center space-y-6 py-4">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircleIcon className="w-10 h-10" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Super Admin Created!</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        One-time password has been generated for <strong>{newEmail}</strong>
                      </p>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-lg font-bold text-indigo-700 tracking-wider">
                        {generatedOTP}
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        In production, this OTP would be emailed securely. Share it with the admin now.
                      </p>
                    </div>

                    <button
                      onClick={() => { 
                        setShowCreateModal(false); 
                        setGeneratedOTP(null); 
                      }}
                      className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition w-full"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}